import React, { useEffect } from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    Outlet,
} from 'react-router-dom';

import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard/Dashboard';

import StatisticsPage from './components/Dashboard/pages/StatisticsPage';
import NSPage from './components/Dashboard/pages/NSPage';
import DevicesPage from './components/Dashboard/pages/DevicesPage';
import ReportsPage from './components/Dashboard/pages/ReportsPage/ReportsPage';
import ArchiveReportPage from './components/Dashboard/pages/ReportsPage/ArchiveReportPage';
import EnergyConsumptionReportPage from './components/Dashboard/pages/ReportsPage/EnergyConsumptionReportPage';
import MapPage from './components/Dashboard/pages/MapPage/MapPage';
import HeatSchedulePage from './components/Dashboard/pages/HeatSchedulePage';
import AnalysisPage from './components/Dashboard/pages/analysis/AnalysisPage';
import MainSettingsPage from './components/Dashboard/pages/MainSettingsPage';
import AdminPage from './components/Dashboard/pages/AdminPage';

import {
    getToken,
    isTokenExpired,
    clearAuth,
    redirectToLogin,
} from './utils/auth';

import { setupAxiosAuthRedirect } from './utils/axios-auth';

// Инициализируем глобальный обработчик 401 для axios
setupAxiosAuthRedirect();

const ProtectedRoute = ({ children }) => {
    const token = getToken();

    // Если токена нет или он уже просрочен — отправляем на логин
    if (!token || isTokenExpired(token)) {
        clearAuth();
        return <Navigate to="/login" replace />;
    }

    return children || <Outlet />;
};

/**
 * Дополнительно периодически проверяем токен.
 * Это нужно для случая, когда пользователь долго держит вкладку открытой
 * и не делает никаких запросов к API.
 */
const TokenExpirationWatcher = () => {
    useEffect(() => {
        const checkToken = () => {
            const token = getToken();

            if (token && isTokenExpired(token)) {
                redirectToLogin();
            }
        };

        checkToken();

        const interval = setInterval(checkToken, 30000);

        const onWindowFocus = () => {
            checkToken();
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkToken();
            }
        };

        window.addEventListener('focus', onWindowFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onWindowFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    return null;
};

function App() {
    return (
        <Router>
            <TokenExpirationWatcher />

            <Routes>
                <Route path="/login" element={<LoginPage />} />

                {/* Защищённый Layout */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Navigate to="/dashboard" replace />} />

                    <Route path="dashboard" element={<StatisticsPage />} />
                    <Route path="ns" element={<NSPage />} />
                    <Route path="devices" element={<DevicesPage />} />
                    <Route path="map" element={<MapPage />} />
                    <Route path="heatschedule" element={<HeatSchedulePage />} />
                    <Route path="analysis" element={<AnalysisPage />} />
                    <Route path="mainsettings" element={<MainSettingsPage />} />
                    <Route path="admin" element={<AdminPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="reports/archive" element={<ArchiveReportPage />} />
                    <Route
                        path="reports/energy-consumption"
                        element={<EnergyConsumptionReportPage />}
                    />
                </Route>

                {/* Ловушка для неизвестных маршрутов */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </Router>
    );
}

export default App;